import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Badge,
  Banner,
  Divider,
  Icon,
  Box,
  Modal,
  RadioButton,
} from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';

import { useStore } from '../providers/StoreProvider';
import { billingApi, api } from '../utils/api';
import AppLayout from '../components/AppLayout';

function Billing() {
  const navigate = useNavigate();
  const {
    billing,
    isPremium,
    isInTrial,
    refreshData,
    plans,
    isAuthenticated,
    shop,
  } = useStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showBarSelectModal, setShowBarSelectModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeBars, setActiveBars] = useState([]);
  const [selectedBarId, setSelectedBarId] = useState(null);

  // Billing state changes out-of-band - Shopify approval redirects, webhooks,
  // admin-granted plans - so the cached snapshot from StoreProvider can be
  // arbitrarily stale by the time the merchant lands here. Always refetch
  // once auth and shop are ready, so /api/store actually goes through.
  // Firing on mount with refreshData() alone fails because the closure may
  // run before AuthProvider resolves shop, causing api.get to no-op.
  // We can't rely on a ?plan=premium marker because Shopify's iframe handoff
  // may strip custom query params during the admin → app-iframe transition.
  const didRefreshRef = React.useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !shop || didRefreshRef.current) return;
    didRefreshRef.current = true;
    // Force a token exchange upfront so /auth/token completes before the
    // refresh fan-out goes out - avoids the 401 → retry dance every time
    // the merchant lands here after a Shopify-approval redirect.
    (async () => {
      await api.ensureFreshToken();
      refreshData();
    })();

    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'subscription_failed') {
      setError('Subscription could not be activated. Please try again.');
      params.delete('error');
      params.delete('plan');
      const qs = params.toString();
      const nextUrl =
        window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState(null, '', nextUrl);
    }
  }, [isAuthenticated, shop, refreshData]);

  const handleSubscribe = useCallback(async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Don't pass a return_url. The backend defaults to /billing/callback,
      // which activates the subscription and then redirects the merchant
      // back into the embedded admin. Generating the return URL on the
      // frontend is fragile because window.location.search may not contain
      // ?shop= inside the Shopify iframe, which would break the redirect.
      const data = await billingApi.subscribe();

      if (data.confirmation_url) {
        // Redirect to Shopify billing approval
        window.top.location.href = data.confirmation_url;
      }
    } catch (err) {
      setError('Failed to start subscription. Please try again.');
      setIsProcessing(false);
    }
  }, []);

  const handleCancel = useCallback(async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const preCheck = await billingApi.preCancel();

      if (preCheck.requires_selection) {
        setActiveBars(preCheck.active_bars);
        setSelectedBarId(preCheck.active_bars[0]?.id || null);
        setShowBarSelectModal(true);
        setIsProcessing(false);
        return;
      }

      // Show confirmation modal instead of browser confirm
      setShowConfirmModal(true);
      setIsProcessing(false);
    } catch (err) {
      setError('Failed to check subscription status. Please try again.');
      setIsProcessing(false);
    }
  }, []);

  const handleSimpleDowngrade = useCallback(async () => {
    setIsProcessing(true);
    setError(null);

    try {
      await billingApi.cancel();
      await refreshData();
      setShowConfirmModal(false);
    } catch (err) {
      setError('Failed to cancel subscription. Please try again.');
    }

    setIsProcessing(false);
  }, [refreshData]);

  const handleConfirmDowngrade = useCallback(async () => {
    if (!selectedBarId) return;

    setIsProcessing(true);
    setError(null);

    try {
      await billingApi.cancel(selectedBarId);
      await refreshData();
      setShowBarSelectModal(false);
      setActiveBars([]);
      setSelectedBarId(null);
    } catch (err) {
      setError('Failed to cancel subscription. Please try again.');
    }

    setIsProcessing(false);
  }, [selectedBarId, refreshData]);

  const basicPlan = plans.find(p => p.id === 'basic');
  const premiumPlan = plans.find(p => p.id === 'premium');

  const activeDiscount = billing?.active_discount;
  const listPrice = Number(billing?.premium_price ?? premiumPlan?.price ?? 8.99);
  const effectivePrice = Number(billing?.effective_price ?? listPrice);
  const effectiveTrialDays = billing?.effective_trial_days
    ?? premiumPlan?.trial_days
    ?? billing?.trial_days
    ?? 7;
  const hasPriceDiscount = activeDiscount && effectivePrice < listPrice;
  const trialUsed = !!billing?.trial_used;
  const trialEligible = !trialUsed && effectiveTrialDays > 0;

  // Build the "you'll keep premium until..." copy for cancel modals.
  // billing.trial_ends_at is the grace-period end for both trial and paid
  // cancellations (Shopify currentPeriodEnd); the wording shifts based on
  // whether the merchant is currently in trial.
  const graceEndDate = billing?.trial_ends_at
    ? new Date(billing.trial_ends_at).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  const graceDaysRemaining = billing?.trial_days_remaining;
  const gracePeriodNote = graceEndDate
    ? isInTrial
      ? `You'll keep premium access until your trial ends on ${graceEndDate}` +
        (graceDaysRemaining != null
          ? ` (${graceDaysRemaining} day${graceDaysRemaining !== 1 ? 's' : ''} remaining)`
          : '') +
        '. After that you\'ll move to the Basic plan. No charge will be made.'
      : `You'll keep premium access until the end of your current billing period on ${graceEndDate}` +
        (graceDaysRemaining != null
          ? ` (${graceDaysRemaining} day${graceDaysRemaining !== 1 ? 's' : ''} remaining)`
          : '') +
        '. After that you\'ll move to the Basic plan and won\'t be billed again.'
    : "Your premium features will remain active until the end of your current billing period.";

  return (
    <AppLayout>
      <Page
        title="Billing"
        backAction={{
          content: 'Back',
          onAction: () => navigate('/app'),
        }}
      >
        {error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Box>
        )}

        {/* Current Plan Banner */}
        {isPremium && (
          <Box paddingBlockEnd="400">
            <Banner
              title={
                billing?.subscription_status === 'cancelled'
                  ? 'Premium Access - Ending Soon'
                  : isInTrial ? 'Premium Trial Active' : 'Premium Plan Active'
              }
              tone={billing?.subscription_status === 'cancelled' ? 'warning' : 'success'}
            >
              {billing?.subscription_status === 'cancelled' ? (
                <p>
                  Your subscription has been cancelled. You still have premium access until{' '}
                  {billing?.trial_ends_at
                    ? new Date(billing.trial_ends_at).toLocaleDateString()
                    : 'soon'
                  }
                  {billing?.trial_days_remaining != null &&
                    ` (${billing.trial_days_remaining} day${billing.trial_days_remaining !== 1 ? 's' : ''} remaining)`
                  }.
                </p>
              ) : isInTrial ? (
                <p>
                  Your premium trial access ends on{' '}
                  {billing?.trial_ends_at
                    ? new Date(billing.trial_ends_at).toLocaleDateString()
                    : 'soon'
                  }
                  {billing?.trial_days_remaining != null &&
                    ` (${billing.trial_days_remaining} day${billing.trial_days_remaining !== 1 ? 's' : ''} remaining)`
                  }.
                  {' '}After your trial, you'll be billed{' '}
                  {hasPriceDiscount
                    ? `$${effectivePrice.toFixed(2)}/month for the first month, then $${listPrice.toFixed(2)}/month`
                    : `$${listPrice.toFixed(2)}/month`}
                  {' '}unless you cancel before then.
                </p>
              ) : (
                <p>You have access to all premium features.</p>
              )}
            </Banner>
          </Box>
        )}

        <div style={{ display: 'flex', gap: '16px' }}>
          {/* Basic Plan */}
          <div style={{ flex: 1 }}>
            <Card background={isPremium ? 'bg-surface-secondary' : undefined}>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingLg">Basic Plan</Text>
                  {!isPremium && <Badge tone="success">Current Plan</Badge>}
                </InlineStack>

                <Text variant="heading2xl">{basicPlan?.price_display || 'Free'}</Text>

                <Divider />

                <Text variant="headingSm">Features included:</Text>

                <BlockStack gap="200">
                  {basicPlan?.features?.map((feature, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text>{feature}</Text>
                    </div>
                  ))}
                </BlockStack>

                <Divider />

                <Text tone="subdued">Limit: 1 active bar</Text>

                {isPremium && billing?.subscription_status !== 'cancelled' && (
                  <Button onClick={handleCancel} loading={isProcessing}>
                    Downgrade to Basic
                  </Button>
                )}
              </BlockStack>
            </Card>
          </div>

          {/* Premium Plan */}
          <div style={{ flex: 1 }}>
            <Card background={!isPremium ? 'bg-surface-secondary' : undefined}>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingLg">Premium Plan</Text>
                  {isPremium && <Badge tone="success">Current Plan</Badge>}
                  {!isPremium && <Badge tone="attention">Recommended</Badge>}
                </InlineStack>

                <InlineStack gap="200" blockAlign="baseline">
                  {hasPriceDiscount ? (
                    <>
                      <Text variant="heading2xl">${effectivePrice.toFixed(2)}/month</Text>
                      <Text tone="subdued" textDecorationLine="line-through">
                        ${listPrice.toFixed(2)}
                      </Text>
                    </>
                  ) : (
                    <Text variant="heading2xl">${listPrice.toFixed(2)}/month</Text>
                  )}
                  {trialEligible && (
                    <Badge tone="info">{effectiveTrialDays}-day free trial</Badge>
                  )}
                  {trialUsed && !isPremium && (
                    <Badge tone="attention">Free trial used</Badge>
                  )}
                </InlineStack>

                {activeDiscount && (
                  <Banner tone="success">
                    <p>
                      {activeDiscount.description ||
                        (activeDiscount.discount_type === 'percentage'
                          ? `${activeDiscount.discount_value}% off`
                          : activeDiscount.discount_type === 'free_trial'
                          ? `Extra ${activeDiscount.discount_value} trial days`
                          : 'Discount applied')}
                      {activeDiscount.expires_at &&
                        ` - expires ${new Date(activeDiscount.expires_at).toLocaleDateString()}`}
                    </p>
                  </Banner>
                )}

                <Divider />

                <Text variant="headingSm">Everything in Basic, plus:</Text>

                <BlockStack gap="200">
                  {premiumPlan?.features?.slice(1).map((feature, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text>{feature}</Text>
                    </div>
                  ))}
                </BlockStack>

                <Divider />

                <Text tone="subdued">Unlimited active bars</Text>

                {!isPremium && (
                  <Button
                    variant="primary"
                    onClick={handleSubscribe}
                    loading={isProcessing}
                  >
                    {trialEligible
                      ? `Start ${effectiveTrialDays}-Day Free Trial`
                      : 'Upgrade to Premium'}
                  </Button>
                )}
              </BlockStack>
            </Card>
          </div>
        </div>

        {/* FAQ */}
        <Box paddingBlockStart="400" />
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd">Frequently Asked Questions</Text>

                <BlockStack gap="300">
                  <Text variant="headingSm">
                    {trialEligible ? 'What happens after the trial?' : 'When will I be billed?'}
                  </Text>
                  <Text tone="subdued">
                    {trialEligible
                      ? `After your ${effectiveTrialDays}-day trial, you'll be automatically charged $${effectivePrice.toFixed(2)}/month. You can cancel anytime before the trial ends.`
                      : `You'll be charged $${effectivePrice.toFixed(2)}/month starting immediately when you upgrade. Your free trial was used on a previous subscription, so a new trial isn't available. You can cancel anytime.`}
                  </Text>
                </BlockStack>

                <BlockStack gap="300">
                  <Text variant="headingSm">Can I cancel anytime?</Text>
                  <Text tone="subdued">
                    Yes! You can downgrade to the Basic plan at any time.
                    Your premium features will remain active until the end
                    of your current billing period.
                  </Text>
                </BlockStack>

                <BlockStack gap="300">
                  <Text variant="headingSm">How is billing handled?</Text>
                  <Text tone="subdued">
                    All billing is handled securely through Shopify.
                    Charges appear on your regular Shopify invoice.
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
        {/* Bar Selection Modal for Downgrade */}
        <Modal
          open={showBarSelectModal}
          onClose={() => {
            setShowBarSelectModal(false);
            setActiveBars([]);
            setSelectedBarId(null);
          }}
          title="Choose a bar to keep active"
          primaryAction={{
            content: 'Downgrade and keep selected bar',
            onAction: handleConfirmDowngrade,
            loading: isProcessing,
            disabled: !selectedBarId,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setShowBarSelectModal(false);
                setActiveBars([]);
                setSelectedBarId(null);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Banner tone="info">
                <p>{gracePeriodNote}</p>
              </Banner>
              <Banner tone="warning">
                <p>
                  The Basic plan only allows 1 active bar. You have{' '}
                  {activeBars.length} active bars. Please choose which bar to keep
                  active. All other bars will be deactivated but not deleted.
                </p>
              </Banner>
              <BlockStack gap="200">
                {activeBars.map((bar) => (
                  <div
                    key={bar.id}
                    onClick={() => setSelectedBarId(bar.id)}
                    style={{
                      padding: '12px',
                      border: selectedBarId === bar.id ? '2px solid var(--p-color-border-emphasis)' : '1px solid var(--p-color-border)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selectedBarId === bar.id ? 'var(--p-color-bg-surface-selected)' : undefined,
                    }}
                  >
                    <InlineStack gap="300" blockAlign="center">
                      <RadioButton
                        checked={selectedBarId === bar.id}
                        onChange={() => setSelectedBarId(bar.id)}
                        label=""
                      />
                      <BlockStack gap="100">
                        <Text variant="bodyMd" fontWeight="semibold">
                          {bar.name}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                          Threshold: ${bar.free_shipping_threshold} | Position:{' '}
                          {bar.position === 'top_sticky' ? 'Top (Sticky)'
                            : bar.position === 'bottom_sticky' ? 'Bottom (Sticky)'
                            : bar.position}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                  </div>
                ))}
              </BlockStack>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Simple Confirmation Modal for Downgrade */}
        <Modal
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Downgrade to Basic Plan?"
          primaryAction={{
            content: 'Yes, downgrade',
            onAction: handleSimpleDowngrade,
            loading: isProcessing,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowConfirmModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text>
                Are you sure you want to cancel your Premium subscription and downgrade to the Basic plan?
              </Text>
              <Text tone="subdued">
                {gracePeriodNote}
              </Text>
              <Text tone="subdued">
                You'll lose access to premium features like tiered rewards, cart drawer placement, unlimited bars, A/B testing, analytics, and geo targeting once you move to Basic.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppLayout>
  );
}

export default Billing;
